import { Box } from "@biggeo/bg-ui/lab";
import * as A from "fp-ts/lib/Array";
import { pipe } from "fp-ts/lib/function";
import isEmpty from "lodash/isEmpty";
import isNil from "lodash/isNil";
import React, { useMemo, useState } from "react";
import { useDispatch } from "react-redux";
import { SetURLSearchParams } from "react-router-dom";

import OnMapLeave from "../../../common/components/OnMapLeave";
import { Consumers } from "../../../common/redux/model";
import { MapLayout, MapLayoutTabs } from "../../../layouts/MapLayout";
import { toasterActions } from "../../../toaster/containers/redux/model";
import { MapLayoutLeftContentContainer } from "../../containers/MapLayoutLeftContentContainer";
import DatasetTableSelectedRowsContainer from "../../filter-criteria/containers/DatasetTableSelectedRowsContainer";
import { MainDatasetTableContainer } from "../../filter-criteria/containers/MainDatasetTableContainer";
import MapTopbar from "../../filter-criteria/views/MapTopbar";
import {
    MapTableTabs,
    MapTableTabsType,
    MapTabs,
} from "../../map-wrappers/MapViewWrapper";
import { getMapFeatures } from "../../utils/geometry-utils";
import { FunctionType } from "../../utils/type";
import MapShapeLayers from "../../views/MapShapeLayers";
import MapStyles, { DEFAULT_MAP_STYLE } from "../../views/MapStyles";
import { SavedAreasSubmenu } from "../../views/SavedAreasSubmenu";
import { useMap } from "../context";
import {
    getMultiFilters,
    setDatasetContext,
} from "../context/utils/general-utils";
import { getMapTab, handleDatasetTable } from "../utils/map-utils";
import { MapboxMapContainer } from "./MapContainer";
import GenerateFileContainer from "./GenerateFileContainer";
import { useMapStateUtilities, usePolygon } from "../context/hooks";
import { AuroraClient } from "../../../utils/socket";
import {
    getInputPolygon,
    pickGeospatialSelection,
} from "../context/utils/map-utils";

export interface IMapMainContainer extends MapTableTabsType {
    readonly mainTab?: MapTabs;
    readonly mapTemplateId: string;
    readonly handleSideMenu?: (v: boolean) => void;
    readonly setOpenSaveViewPopper: React.Dispatch<
        React.SetStateAction<boolean>
    >;
    readonly openSaveViewPopper: boolean;
    readonly isGlobalTemplate?: boolean;
    readonly savedAreaId?: string;
    readonly savedViewId?: string;
    readonly openGenerateFilePage: boolean;
    readonly setOpenGenerateFilePage: React.Dispatch<
        React.SetStateAction<boolean>
    >;
    readonly selectedSavedView?: number;
    readonly isFromMarketplace?: boolean;
    readonly setSearchParams: SetURLSearchParams;
    readonly searchParams: URLSearchParams;
    readonly sockets: readonly AuroraClient[];
    readonly channelId: string;
}

const MapMainContainer = ({
    mapTemplateId,
    isGlobalTemplate = false,
    savedAreaId,
    savedViewId,
    searchParams,
    setSearchParams,
    tab,
    setTab,
    openGenerateFilePage,
    setOpenGenerateFilePage,
    sockets,
    channelId,
    ...props
}: IMapMainContainer) => {
    const reduxDispatch = useDispatch();
    const isSavedViewPage = !isNil(savedViewId);

    const multiFilters = getMultiFilters();

    const {
        map,
        isMapLoaded,
        draw,
        modes,
        datasets,
        polygons,
        viewport,
        functionType,
    } = useMap();

    const bounds = map.current?.getBounds();

    const {
        addDatasets,
        updateDataset,
        removeDataset,
        overrideDatasets,
        reorderDatasets,
    } = setDatasetContext();

    const { setFunctionType } = useMapStateUtilities();

    const { deletePolygon, clearSelectedPolygons } = usePolygon();

    const [navTab, setNavTab] = useState<MapLayoutTabs>(
        getMapTab(searchParams.get("mapTab"))
    );

    const [mapStyle, setMapStyle] = useState(DEFAULT_MAP_STYLE.key);

    const handleMapTableTab = (t: MapTableTabs) => {
        setTab(t);

        handleDatasetTable({
            state: t === MapTableTabs.split || t === MapTableTabs.table,
            datasets,
            updateDataset,
        });
    };

    const isTopMapBarOpen =
        !isEmpty(datasets.filter((d) => !!d.isSelected)) || !isEmpty(polygons);

    const datasetsViewed = useMemo(
        () =>
            pipe(
                datasets,
                A.filter((d) => d.isTableViewed)
            ),
        [datasets]
    );

    const isDatasetTableOpen =
        !isEmpty(datasetsViewed) && tab === MapTableTabs.split;

    const geospatialSelection = pickGeospatialSelection({
        viewport,
        multipolygon: polygons.map((c) => {
            const input = getInputPolygon(c.feature);

            return {
                inners: input.inners,
                outer: input.outer,
            };
        }),
    });

    const exitMapModes = (tab: MapLayoutTabs) => {
        if (tab === "shapeLayers") {
            setFunctionType(FunctionType.viewport);
            draw.current?.changeMode("simple_select");

            clearSelectedPolygons();

            if (modes?.select.isSelectMode) {
                modes.select.selectMode(false);
                draw.current?.add({
                    type: "FeatureCollection",
                    features: pipe(
                        getMapFeatures(draw, isMapLoaded),
                        A.map((f) => ({
                            ...f,
                            properties: {
                                ...f.properties,
                                selected: false,
                            },
                        }))
                    ),
                });
            }
        }
        if (tab !== "shapeLayers") {
            reduxDispatch(
                toasterActions.openMapPopup({
                    sx: { display: "none" },
                })
            );
        }
    };

    return (
        <OnMapLeave>
            <MapLayout
                leftOpen
                topOpen={isTopMapBarOpen}
                bottomOpen={isDatasetTableOpen}
                sidebarTab={navTab}
                top={
                    <MapTopbar
                        currentTab={tab}
                        datasets={datasets}
                        updateDataset={updateDataset}
                        setTab={handleMapTableTab}
                        deletePolygon={deletePolygon}
                        setNavTab={setNavTab}
                    />
                }
                bottom={({ hasTransitioned }) =>
                    isDatasetTableOpen ? (
                        <MainDatasetTableContainer
                            tab={tab}
                            datasets={datasetsViewed}
                            channelId={channelId}
                            updateDataset={updateDataset}
                            geospatialSelection={geospatialSelection}
                            sockets={sockets}
                            hasTransitioned={hasTransitioned}
                        />
                    ) : undefined
                }
                datasets={
                    <MapLayoutLeftContentContainer
                        map={map}
                        isMapLoaded={isMapLoaded}
                        mapTemplateId={mapTemplateId}
                        isGlobalTemplate={isGlobalTemplate}
                        addDatasets={addDatasets}
                        updateDataset={updateDataset}
                        removeDataset={removeDataset}
                        isSavedViewPage={isSavedViewPage}
                        overrideDatasets={overrideDatasets}
                        reorderDatasets={reorderDatasets}
                    />
                }
                shapeLayers={
                    <MapShapeLayers
                        deletePolygon={deletePolygon}
                        functionType={functionType}
                        exitMapModes={() => exitMapModes("shapeLayers")}
                    />
                }
                boundaries={
                    <SavedAreasSubmenu
                        savedAreaId={savedAreaId}
                        mapTemplateId={mapTemplateId}
                        isSavedViewPage={isSavedViewPage}
                    />
                }
                maps={
                    <MapStyles
                        isMapLoaded={isMapLoaded}
                        map={map}
                        currentStyle={
                            map.current && isMapLoaded
                                ? map.current.getStyle()
                                : undefined
                        }
                        onStyleChange={setMapStyle}
                        draw={draw}
                        datasets={datasets}
                    />
                }
                onSidebarOpenChange={(_, tab) => {
                    exitMapModes(tab);
                    setNavTab(tab);
                }}
                onSidebarItemClick={(tab) => {
                    exitMapModes(tab);
                    setNavTab(tab);
                }}
            >
                {tab === MapTableTabs.table && datasetsViewed.length > 0 ? (
                    <MainDatasetTableContainer
                        tab={tab}
                        datasets={datasetsViewed}
                        channelId={channelId}
                        updateDataset={updateDataset}
                        geospatialSelection={geospatialSelection}
                        sockets={sockets}
                        hasTransitioned
                    />
                ) : (
                    <Box
                        height={"100%"}
                        width={"100%"}
                        sx={{
                            padding: 4,
                            borderRadius: (theme) => theme.radius.xs3,
                        }}
                    >
                        {openGenerateFilePage ? (
                            <GenerateFileContainer
                                openGenerateFilePage={openGenerateFilePage}
                                setOpenGenerateFilePage={
                                    setOpenGenerateFilePage
                                }
                                bounds={bounds}
                                viewport={viewport}
                                draw={draw}
                                savedViewId={savedViewId}
                                isMapLoaded={isMapLoaded}
                                mapStyle={mapStyle}
                                map={map}
                                datasets={datasets}
                                polygons={polygons}
                            />
                        ) : (
                            <MapboxMapContainer
                                key={Consumers.mapbox}
                                {...props}
                                mapStyle={mapStyle}
                                multiFilters={multiFilters}
                                channelId={channelId}
                                savedViewId={savedViewId}
                                setSearchParams={setSearchParams}
                                mapTemplateId={mapTemplateId}
                                setNavTab={setNavTab}
                                openGenerateFilePage={openGenerateFilePage}
                                setOpenGenerateFilePage={
                                    setOpenGenerateFilePage
                                }
                            />
                        )}
                    </Box>
                )}
                <DatasetTableSelectedRowsContainer />
            </MapLayout>
        </OnMapLeave>
    );
};

export default MapMainContainer;
